import './PdfViewer.scss';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

import * as Sentry from '@sentry/react';
import ErrorAlert from 'components/ErrorAlert/ErrorAlert';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import { getAccessToken } from 'helpers/tokenManager';
import type { ComponentProps } from 'react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';

type DocumentCallback = Parameters<
  NonNullable<ComponentProps<typeof Document>['onLoadSuccess']>
>[0];

interface Props {
  file: string;
  getDocument?: (document: Blob) => void;
}

const PdfViewer: React.FC<Props> = (props): React.ReactElement => {
  const { file, getDocument } = props;

  const [numPages, setNumPages] = useState<number>(0);
  const [pageWidth, setPageWidth] = useState<number>(850);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const resizePage = () => {
      const containerWidth = containerRef.current?.clientWidth;

      if (!containerWidth) {
        return;
      }

      setPageWidth(Math.min(850, Math.max(280, Math.floor(containerWidth))));
    };

    resizePage();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(resizePage);

      if (containerRef.current) {
        observer.observe(containerRef.current);
      }

      return () => observer.disconnect();
    }

    window.addEventListener('resize', resizePage);

    return () => window.removeEventListener('resize', resizePage);
  }, []);

  const onLoad = async (pdf: DocumentCallback) => {
    if (getDocument) {
      const data = await pdf.getData();
      const blob = new Blob([data as any], { type: 'application/pdf' });

      getDocument(blob);
    }

    setNumPages(pdf.numPages);
  };

  const renderPages = () => {
    const pages = [];

    for (let index = 1; index <= numPages; index++) {
      pages.push(
        <Page
          loading={<LoadingIndicator />}
          pageNumber={index}
          width={pageWidth}
          className="PdfViewer_page"
          key={index}
        />
      );
    }

    return pages;
  };

  const options = useMemo(
    () => ({ httpHeaders: { Authorization: `Bearer ${getAccessToken()}` } }),
    []
  );

  return (
    <div className="PdfViewer_container" ref={containerRef}>
      <Document
        file={file}
        onLoadSuccess={onLoad}
        options={options}
        loading={<LoadingIndicator />}
        className="PdfViewer"
        error={
          <ErrorAlert errorCode="errors.loadDocumentFailed" onRetry={() => location.reload()} />
        }
        onLoadError={(error) => Sentry.captureException(error)}
      >
        {renderPages()}
      </Document>
    </div>
  );
};

export default PdfViewer;
